// Общий «Контекст» разделов: дата · глубина · вид. Единая (де)сериализация в
// query-параметры. Эти три ключа принадлежат КОНТЕКСТУ (не странице): разделы
// «Карты»/«Аналитика» читают их из общего стора и переживают переключение между
// собой; в URL их пишет ровно один писатель — контекст.

import { DEPTH_LEVELS } from '../api'
import { parseFleet, serializeFleet, type FleetSegment } from '../models/fleetSegments'
import { WAVE_STEP_MIN, WAVE_STEP_MAX, WAVE_STEP_DEFAULT } from '../map/waveContours'
import { parseFlags, serializeFlags } from '../models/flagFilter'

export interface ViewCtx {
  date: string
  depth: number
  species: number | null
}

// ── Блок «Наложения»: оверлеи + флот + волнение + «суда без категорий» ──
// Единая (де)сериализация этих ключей: раньше overlays писала только страница
// «Карты», fleet — только «Модели», а волнение — никто. Теперь оба раздела пишут
// один и тот же набор через writeOverlaysUrl, а ViewContext им же СИДится из URL —
// рефреш/шаринг восстанавливают ровно те наложения, что были включены.

export interface OverlaysUrlState {
  overlays: string[]
  fleet: FleetSegment[]
  waves: boolean
  showUncat: boolean
  /** Шаг изолиний волнения, м (дефолт 0.5). Значим только при waves. */
  waveStep: number
  /** Выбор флагов иностранного флота; пусто = все флаги. */
  flags: string[]
}

/** Ключи блока «Наложения» в URL — для чистого слияния (delete-before-write). */
export const OVERLAY_URL_KEYS = [
  'overlays', 'fleet', 'waves', 'showuncat', 'wavestep', 'flags',
] as const

/** Дописывает ключи «Наложений» в набор параметров (мутирует out), НЕ трогая чужие.
 *  Пустое/дефолт → удаляем ключ (чистый URL). Round-trip с parseOverlaysUrl. */
export function writeOverlaysUrl(out: URLSearchParams, s: OverlaysUrlState): void {
  if (s.overlays.length) out.set('overlays', s.overlays.join(','))
  else out.delete('overlays')
  if (s.fleet.length) out.set('fleet', serializeFleet(s.fleet))
  else out.delete('fleet')
  if (s.waves) out.set('waves', '1')
  else out.delete('waves')
  if (s.showUncat) out.set('showuncat', '1')
  else out.delete('showuncat')
  // Шаг пишем только не-дефолтный (0.5 не засоряет URL).
  if (s.waveStep !== WAVE_STEP_DEFAULT) out.set('wavestep', String(s.waveStep))
  else out.delete('wavestep')
  const flags = serializeFlags(s.flags)
  if (flags) out.set('flags', flags)
  else out.delete('flags')
}

/** Читает «Наложения» из URL (сид контекста + парс страниц). Битые/пустые → выкл. */
export function parseOverlaysUrl(params: URLSearchParams): OverlaysUrlState {
  const raw = params.get('overlays')
  const stepRaw = params.get('wavestep')
  const stepNum = stepRaw == null || stepRaw === '' ? NaN : Number(stepRaw)
  const waveStep =
    Number.isFinite(stepNum) && stepNum >= WAVE_STEP_MIN && stepNum <= WAVE_STEP_MAX
      ? stepNum
      : WAVE_STEP_DEFAULT
  return {
    overlays: raw ? raw.split(',').filter(Boolean) : [],
    fleet: parseFleet(params.get('fleet')) ?? [],
    waves: params.get('waves') === '1',
    showUncat: params.get('showuncat') === '1',
    waveStep,
    flags: parseFlags(params.get('flags')),
  }
}

// Единый дефолт глубины для всех разделов = 30 м. Решение владельца 2026-07-25
// («Аналитика» открывается на 30 м) распространено на общий контекст. Слои без
// глубины (SST, батиметрия, ssha, оверлеи) её всё равно игнорируют — гасит
// depthApplicable, поэтому единый дефолт безопасен.
export const DEFAULT_DEPTH = 30

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Читает контекст из URL (сид при монтировании: шаринг/рефреш открывают тот же вид).
 *  Битые/пустые значения → дефолты (today передаётся снаружи — Date не в чистой функции). */
export function parseViewCtx(params: URLSearchParams, today: string): ViewCtx {
  const rawDepth = params.get('depth')
  const depthNum = rawDepth == null || rawDepth === '' ? NaN : Number(rawDepth)
  const rawSpecies = params.get('species')
  const speciesNum = rawSpecies == null || rawSpecies === '' ? NaN : Number(rawSpecies)
  const date = params.get('date')
  return {
    date: date && DATE_RE.test(date) ? date : today,
    depth: DEPTH_LEVELS.includes(depthNum) ? depthNum : DEFAULT_DEPTH,
    species: Number.isInteger(speciesNum) && speciesNum >= 0 ? speciesNum : null,
  }
}

/** Пишет ТОЛЬКО свои три ключа, не затирая чужие (layer/model/lat/lng/overlays/…):
 *  контекст — единственный писатель date/depth/species (страницы их не пишут). */
export function writeViewCtx(prev: URLSearchParams, ctx: ViewCtx): URLSearchParams {
  const out = new URLSearchParams(prev)
  if (ctx.date) out.set('date', ctx.date)
  else out.delete('date')
  out.set('depth', String(ctx.depth))
  if (ctx.species != null) out.set('species', String(ctx.species))
  else out.delete('species')
  return out
}

/** Вид домена для холодного входа: ставится, только когда ссылка вида не
 *  задаёт и вид ещё не выбран. `null` — ставить нечего. */
export function brandSpeciesSeed(
  params: URLSearchParams,
  current: number | null,
  brandSpecies: number | null | undefined,
): number | null {
  if (params.has('species') || current != null || brandSpecies == null) return null
  return brandSpecies
}
